import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2, Check, ChevronLeft, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { addCardNative, isNativeStripeAvailable } from "@/lib/stripeNative";

/**
 * ClientPaymentMethods — "Mes cartes" screen.
 *
 *   • Lists every saved card (brand + last4 + expiry, PCI-tokenised
 *     server-side — the full PAN never touches our backend).
 *   • "Ajouter une carte" opens the native Stripe Payment Flow (SetupIntent)
 *     which enrols the card without charging. Falls back to a friendly
 *     message on the web preview.
 *   • Tap a card → set as default (used silently at next booking).
 *   • Trash icon → detach from the customer.
 *
 * Design language matches the Client dashboard : Outfit headings, soft
 * cards, generous spacing, single accent colour (primary teal).
 */
const CARD_BRANDS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners",
  jcb: "JCB",
  unionpay: "UnionPay",
};

function BrandBadge({ brand }) {
  const label = CARD_BRANDS[String(brand || "").toLowerCase()] || (brand ? brand.toUpperCase() : "CARTE");
  return (
    <span className="rounded-md bg-neutral-900/90 px-2 py-0.5 font-[Outfit] text-[10px] font-semibold uppercase tracking-widest text-white">
      {label}
    </span>
  );
}

export default function ClientPaymentMethods() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/client/payment-methods");
      setCards(data?.cards || []);
      setDefaultId(data?.default_id || null);
    } catch (err) {
      toast.error(formatApiError(err) || "Impossible de charger vos cartes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAdd = async () => {
    if (!isNativeStripeAvailable()) {
      toast.info("Ajoutez une carte depuis l'app iOS ou Android — la version web ne le permet pas encore.");
      return;
    }
    setAdding(true);
    try {
      const res = await addCardNative();
      if (res.success) {
        toast.success("Carte enregistrée pour vos prochaines courses");
        await refresh();
      } else if (res.reason === "cancelled") {
        toast.info("Ajout de carte annulé");
      } else {
        toast.error("Impossible d'enregistrer la carte");
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Erreur Stripe");
    } finally {
      setAdding(false);
    }
  };

  const handleSetDefault = async (pmId) => {
    if (pmId === defaultId) return;
    setBusyId(pmId);
    try {
      await api.post(`/client/payment-methods/${pmId}/default`);
      setDefaultId(pmId);
      toast.success("Carte définie par défaut");
    } catch (err) {
      toast.error(formatApiError(err) || "Impossible de définir par défaut");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (pmId) => {
    if (!window.confirm("Supprimer cette carte ?")) return;
    setBusyId(pmId);
    try {
      await api.delete(`/client/payment-methods/${pmId}`);
      toast.success("Carte supprimée");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Suppression impossible");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-secondary/30 to-white pb-24"
      style={{ paddingTop: "max(24px, env(safe-area-inset-top))" }}
      data-testid="payment-methods-page"
    >
      <header className="px-5 pb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm font-medium text-foreground shadow-sm backdrop-blur transition hover:bg-white"
          data-testid="back-btn"
        >
          <ChevronLeft className="h-4 w-4" /> Retour
        </button>
        <div className="mt-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.24em] text-primary/70">Saor · Paiement</p>
          <h1 className="mt-1 font-[Outfit] text-3xl font-semibold tracking-tight text-foreground">Mes cartes</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enregistrez vos cartes pour payer vos courses en 1 tap.
          </p>
        </div>
      </header>

      <section className="mx-5 rounded-3xl border border-border/60 bg-white p-4 shadow-[0_10px_30px_-15px_rgba(15,38,52,0.15)]">
        {loading ? (
          <div className="grid place-items-center py-10 text-sm text-muted-foreground">Chargement…</div>
        ) : cards.length === 0 ? (
          <div className="grid place-items-center py-10 text-center">
            <CreditCard className="mb-3 h-10 w-10 text-primary/60" />
            <p className="font-[Outfit] text-lg font-semibold text-foreground">Aucune carte enregistrée</p>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">
              Ajoutez-en une pour bénéficier du paiement en 1 tap à vos prochaines courses.
            </p>
          </div>
        ) : (
          <ul className="grid gap-2">
            {cards.map((c) => {
              const isDefault = c.id === defaultId;
              return (
                <li
                  key={c.id}
                  className={`group relative flex items-center gap-3 rounded-2xl border p-3 transition ${
                    isDefault
                      ? "border-primary/60 bg-primary/5 shadow-inner"
                      : "border-border/60 bg-white hover:border-primary/30"
                  }`}
                  data-testid={`card-${c.last4}`}
                >
                  <button
                    type="button"
                    onClick={() => handleSetDefault(c.id)}
                    disabled={busyId === c.id}
                    className="flex flex-1 items-center gap-3 text-left"
                    data-testid={`set-default-${c.last4}`}
                  >
                    <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-neutral-900/90 text-white">
                      <CreditCard className="h-5 w-5" />
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <BrandBadge brand={c.brand} />
                        {isDefault && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary">
                            <Check className="h-3 w-3" /> Par défaut
                          </span>
                        )}
                      </div>
                      <p className="mt-1 font-[Outfit] text-base font-semibold tracking-tight text-foreground tabular-nums">
                        •••• {c.last4}
                      </p>
                      <p className="text-[11px] text-muted-foreground tabular-nums">
                        Expire {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(c.id)}
                    disabled={busyId === c.id}
                    className="grid h-9 w-9 flex-none place-items-center rounded-full text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                    aria-label="Supprimer"
                    data-testid={`delete-${c.last4}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <button
          type="button"
          onClick={handleAdd}
          disabled={adding}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-semibold text-primary-foreground shadow-[0_10px_25px_-8px_hsl(var(--primary)/0.5)] transition active:scale-[0.99] disabled:opacity-60"
          data-testid="add-card-btn"
        >
          <Plus className="h-4 w-4" />
          {adding ? "Ajout en cours…" : "Ajouter une carte"}
        </button>
      </section>

      <p className="mx-5 mt-5 flex items-start gap-2 text-[12px] leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-primary/70" />
        <span>
          Vos cartes sont tokenisées et stockées par <b>Stripe</b> (PCI-DSS niveau 1).
          Saor n&apos;a jamais accès au numéro complet de votre carte.
        </span>
      </p>
    </div>
  );
}
